import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, CheckCircle, XCircle, Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { renderPremiumGuidePdf } from "@/lib/premiumGuidePdf";

interface ResultItem {
  postId: string;
  title: string;
  status: "success" | "failed" | "skipped";
  error?: string;
  fileSize?: string;
}

interface PostRow {
  id: string;
  title: string;
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const BulkPremiumGuideGenerator = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState({ total: 0, withGuide: 0, needing: 0 });
  const [pendingPosts, setPendingPosts] = useState<PostRow[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<ResultItem[]>([]);
  const [delaySec, setDelaySec] = useState(5);
  const [accentColor, setAccentColor] = useState("#fbbf24");
  const [includeReflection, setIncludeReflection] = useState(true);
  const [skipExisting, setSkipExisting] = useState(true);
  const [done, setDone] = useState(false);

  const fetchStats = async () => {
    const { data: posts } = await supabase
      .from("blog_posts")
      .select("id, title")
      .eq("status", "published")
      .order("published_at", { ascending: false });

    const allPosts = (posts || []) as PostRow[];

    const { data: existing } = await supabase
      .from("blog_post_resources" as any)
      .select("blog_post_id, name")
      .ilike("name", "Premium Blueprint%");

    const existingIds = new Set<string>(
      ((existing as any) || []).map((r: any) => r.blog_post_id),
    );

    const needing = allPosts.filter((p) => !existingIds.has(p.id));
    setPendingPosts(needing);
    setStats({
      total: allPosts.length,
      withGuide: existingIds.size,
      needing: needing.length,
    });
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRun = async () => {
    const queue = skipExisting ? pendingPosts : [...pendingPosts];
    if (queue.length === 0) {
      toast({ title: "Nothing to generate", description: "All posts already have a Premium Blueprint." });
      return;
    }

    setIsRunning(true);
    setDone(false);
    setProgress(0);
    setResults([]);

    const collected: ResultItem[] = [];

    for (let i = 0; i < queue.length; i++) {
      const post = queue[i];
      try {
        const { data, error } = await supabase.functions.invoke("generate-premium-guide", {
          body: { blogPostId: post.id },
        });
        if (error) throw error;
        if (!data?.markdown) throw new Error("Empty markdown returned");

        const blob = await renderPremiumGuidePdf(data.markdown, data.title || post.title, {
          subtitle: "Premium Blueprint • RunoBlog Learning System",
          accentColor,
          includeReflection,
        });
        const sizeStr = formatBytes(blob.size);
        const filename = `premium-blueprint-${Date.now()}.pdf`;
        const path = `${post.id}/${filename}`;

        const { error: upErr } = await supabase.storage
          .from("blog-resources")
          .upload(path, blob, { upsert: false, contentType: "application/pdf" });
        if (upErr) throw upErr;
        const { data: pub } = supabase.storage.from("blog-resources").getPublicUrl(path);

        const { error: insErr } = await supabase.from("blog_post_resources" as any).insert({
          blog_post_id: post.id,
          name: `Premium Blueprint — ${data.title || post.title}`,
          description: "Premium implementation blueprint with modules, frameworks, exercises, quiz recap and FAQ.",
          file_url: pub.publicUrl,
          file_format: "PDF",
          file_size: sizeStr,
          order_index: 0,
        });
        if (insErr) throw insErr;

        collected.push({ postId: post.id, title: post.title, status: "success", fileSize: sizeStr });
      } catch (err: any) {
        collected.push({
          postId: post.id,
          title: post.title,
          status: "failed",
          error: err?.message || "Unknown error",
        });
      }

      setResults([...collected]);
      setProgress(Math.round(((i + 1) / queue.length) * 100));

      if (i < queue.length - 1) await wait(delaySec * 1000);
    }

    setDone(true);
    setIsRunning(false);
    const ok = collected.filter((r) => r.status === "success").length;
    const fail = collected.filter((r) => r.status === "failed").length;
    toast({
      title: "Bulk generation complete",
      description: `Generated ${ok} blueprints. ${fail} failed.`,
    });
    fetchStats();
  };

  const successCount = results.filter((r) => r.status === "success").length;
  const failCount = results.filter((r) => r.status === "failed").length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            Bulk Premium Blueprint Generator
          </h1>
          <p className="text-muted-foreground mt-2">
            Generate premium PDF blueprints for every published blog post in one run.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader><CardTitle className="text-sm font-medium">Total Published Posts</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold">{stats.total}</div></CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm font-medium">Posts with Blueprint</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-green-600">{stats.withGuide}</div></CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-sm font-medium">Posts Needing Blueprint</CardTitle></CardHeader>
            <CardContent><div className="text-3xl font-bold text-orange-600">{stats.needing}</div></CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Generation Settings</CardTitle>
            <CardDescription>Control rendering style and pacing.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="delay">Delay Between Posts (seconds)</Label>
                <Input
                  id="delay"
                  type="number"
                  min={2}
                  max={60}
                  value={delaySec}
                  onChange={(e) => setDelaySec(parseInt(e.target.value) || 5)}
                  disabled={isRunning}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="accent">Accent Color</Label>
                <Input
                  id="accent"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  disabled={isRunning}
                  className="h-10"
                />
              </div>
              <div className="space-y-2">
                <Label>Auto Reflection + Weekly Goals</Label>
                <div className="flex items-center gap-3 h-10">
                  <Switch
                    checked={includeReflection}
                    onCheckedChange={setIncludeReflection}
                    disabled={isRunning}
                  />
                  <span className="text-sm text-muted-foreground">
                    {includeReflection ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={skipExisting}
                onCheckedChange={setSkipExisting}
                disabled={isRunning}
              />
              <Label>Skip posts that already have a Premium Blueprint</Label>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Each blueprint takes ~30–60s to generate and render. Keep this tab open until the run finishes.
                Estimated time: {Math.ceil((pendingPosts.length * (45 + delaySec)) / 60)} minutes.
              </AlertDescription>
            </Alert>

            <Button
              onClick={handleRun}
              disabled={isRunning || pendingPosts.length === 0}
              size="lg"
              className="w-full"
            >
              {isRunning ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating Blueprints...</>
              ) : (
                <><Sparkles className="mr-2 h-4 w-4" /> Generate All Missing Blueprints ({pendingPosts.length} posts)</>
              )}
            </Button>
          </CardContent>
        </Card>

        {(isRunning || done) && (
          <Card>
            <CardHeader><CardTitle>Generation Progress</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Overall Progress</span>
                  <span>{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
              {done && (
                <div className="grid gap-2 md:grid-cols-2">
                  <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">{successCount} Successful</div>
                      <div className="text-sm text-muted-foreground">Blueprints saved</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950 rounded-lg">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <div>
                      <div className="font-medium">{failCount} Failed</div>
                      <div className="text-sm text-muted-foreground">Errors occurred</div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Generation Results</CardTitle>
              <CardDescription>Per-post results for this run.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {results.map((r, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      r.status === "success"
                        ? "bg-green-50 dark:bg-green-950"
                        : "bg-red-50 dark:bg-red-950"
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {r.status === "success" ? (
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{r.title}</div>
                        {r.status === "failed" && r.error && (
                          <div className="text-sm text-red-600 dark:text-red-400 truncate">{r.error}</div>
                        )}
                        {r.status === "success" && r.fileSize && (
                          <div className="text-xs text-muted-foreground">{r.fileSize}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default BulkPremiumGuideGenerator;
